import type { ProviderSetup } from '@epilot/sdk/phone-integration'
import { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString() : undefined

const Done = ({ done }: { done: boolean }) => (
  <Badge variant={done ? 'default' : 'outline'}>
    {done ? 'Configured' : 'Not configured'}
  </Badge>
)

const Row = ({
  label,
  status,
  children
}: {
  label: string
  status: ReactNode
  children?: ReactNode
}) => (
  <div className="flex items-start justify-between gap-4 border-b py-3 last:border-b-0">
    <div className="space-y-1">
      <p className="text-sm font-medium">{label}</p>
      {children && (
        <div className="text-sm text-muted-foreground">{children}</div>
      )}
    </div>
    {status}
  </div>
)

/** What phone-integration-api reports as set up at the provider for one organization. */
export const ProviderStatusCard = ({ setup }: { setup: ProviderSetup }) => (
  <Card
    aria-label="Provider setup status"
    className="text-primary"
    role="region"
  >
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1.5">
        <CardTitle className="text-lg">Babelforce</CardTitle>
        <CardDescription>
          {setup.ready
            ? 'Phone agents can be assigned in this organization.'
            : 'Run the setup before assigning phone agents in this organization.'}
        </CardDescription>
      </div>
      <Badge variant={setup.ready ? 'success' : 'outline'}>
        {setup.ready ? 'Ready' : 'Incomplete'}
      </Badge>
    </CardHeader>
    <CardContent>
      <Row
        label="Tenant (sub-account)"
        status={<Done done={Boolean(setup.tenant)} />}
      >
        {setup.tenant ? (
          <>
            <p>
              {setup.tenant.name} · ID {setup.tenant.id}
            </p>
            <p>Organization key: {setup.tenant.org_key}</p>
            {setup.tenant.created_at && (
              <p>Created: {formatDate(setup.tenant.created_at)}</p>
            )}
          </>
        ) : (
          <p>Not created yet.</p>
        )}
      </Row>
      <Row
        label="SCIM user provisioning"
        status={<Done done={setup.scim.configured} />}
      >
        <p>
          Role management {setup.scim.roles_enabled ? 'enabled' : 'disabled'}
        </p>
      </Row>
      <Row
        label="Single sign-on (Sign in with epilot)"
        status={<Done done={setup.sso.configured} />}
      >
        {setup.sso.identity_client_id && (
          <p>Application: {setup.sso.identity_client_id}</p>
        )}
        {setup.sso.configured_at && (
          <p>Configured: {formatDate(setup.sso.configured_at)}</p>
        )}
      </Row>
      <Row
        label="Last setup run by"
        status={
          <span className="text-sm text-muted-foreground">
            {setup.last_setup_by ?? 'Never run'}
          </span>
        }
      />
    </CardContent>
  </Card>
)
